import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, Users, PawPrint, Calendar, Building, Loader2, Edit, Trash2,
  Link2, Unlink, Eye, Heart, Dog, Cat
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Litter Card Component
const LitterCard = ({ litter, onClick, onAddAnimal }) => (
  <Card className="cursor-pointer hover:shadow-md transition-shadow">
    <CardContent className="p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">{litter.litter_name}</h3>
          </div>
          <div className="text-sm text-stone-600 space-y-1">
            {litter.breed && <p>Breed: {litter.breed}</p>}
            {litter.dam_name && <p>Dam: {litter.dam_name}</p>}
            {litter.sire_name && <p>Sire: {litter.sire_name}</p>}
            {litter.birth_date && <p>Born: {litter.birth_date}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge variant="secondary">
            {litter.current_animal_count || 0} animals
          </Badge>
          {litter.breeder_name && (
            <Badge variant="outline" className="text-xs">
              <Building className="w-3 h-3 mr-1" />
              {litter.breeder_name}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onClick(litter); }}>
          <Eye className="w-3 h-3 mr-1" /> View
        </Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onAddAnimal(litter); }}>
          <Plus className="w-3 h-3 mr-1" /> Add Animal
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Create/Edit Litter Dialog
const LitterDialog = ({ open, onClose, onSuccess, litter, animals }) => {
  const [formData, setFormData] = useState({
    litter_name: '',
    species: 'Dog',
    breed: '',
    dam_id: '',
    dam_name: '',
    sire_id: '',
    sire_name: '',
    birth_date: '',
    litter_size: 0,
    breeder_name: '',
    notes: ''
  });
  const [potentialParents, setPotentialParents] = useState({ females: [], males: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (litter) {
      setFormData({
        litter_name: litter.litter_name || '',
        species: litter.species || 'Dog',
        breed: litter.breed || '',
        dam_id: litter.dam_id || '',
        dam_name: litter.dam_name || '',
        sire_id: litter.sire_id || '',
        sire_name: litter.sire_name || '',
        birth_date: litter.birth_date || '',
        litter_size: litter.litter_size || 0,
        breeder_name: litter.breeder_name || '',
        notes: litter.notes || ''
      });
    } else {
      setFormData({
        litter_name: '',
        species: 'Dog',
        breed: '',
        dam_id: '',
        dam_name: '',
        sire_id: '',
        sire_name: '',
        birth_date: '',
        litter_size: 0,
        breeder_name: '',
        notes: ''
      });
    }
  }, [litter, open]);

  useEffect(() => {
    if (open) {
      fetchPotentialParents();
    }
  }, [open, formData.species]);

  const fetchPotentialParents = async () => {
    try {
      const response = await axios.get(`/shelter/animals/potential-parents?species=${formData.species}`);
      setPotentialParents(response.data);
    } catch (error) {
      console.error('Failed to fetch potential parents:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (litter) {
        await axios.put(`/shelter/litters/${litter.litter_id}`, formData);
        toast.success('Litter updated');
      } else {
        await axios.post('/shelter/litters', formData);
        toast.success('Litter created');
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save litter');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{litter ? 'Edit Litter' : 'Create New Litter'}</DialogTitle>
          <DialogDescription>
            Track lineage by creating litters and linking animals to their parents.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Litter Name *</Label>
              <Input
                value={formData.litter_name}
                onChange={(e) => setFormData({...formData, litter_name: e.target.value})}
                placeholder="e.g., Spring 2025 Litter"
                required
              />
            </div>
            <div>
              <Label>Species</Label>
              <Select 
                value={formData.species} 
                onValueChange={(v) => setFormData({...formData, species: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dog">Dog</SelectItem>
                  <SelectItem value="Cat">Cat</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Breed</Label>
              <Input
                value={formData.breed}
                onChange={(e) => setFormData({...formData, breed: e.target.value})}
                placeholder="Breed"
              />
            </div>
            <div>
              <Label>Birth Date</Label>
              <Input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Parents</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Dam (Mother)</Label>
                <Select 
                  value={formData.dam_id || 'none'} 
                  onValueChange={(v) => {
                    const dam = potentialParents.females.find(a => a.animal_id === v);
                    setFormData({
                      ...formData, 
                      dam_id: v === 'none' ? '' : v,
                      dam_name: dam?.name || formData.dam_name
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select dam..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not in system</SelectItem>
                    {potentialParents.females.map((animal) => (
                      <SelectItem key={animal.animal_id} value={animal.animal_id}>
                        {animal.name} ({animal.breed})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(!formData.dam_id || formData.dam_id === 'none') && (
                  <Input
                    className="mt-2"
                    value={formData.dam_name}
                    onChange={(e) => setFormData({...formData, dam_name: e.target.value})}
                    placeholder="Or enter dam name manually"
                  />
                )}
              </div>
              <div>
                <Label>Sire (Father)</Label>
                <Select 
                  value={formData.sire_id || 'none'} 
                  onValueChange={(v) => {
                    const sire = potentialParents.males.find(a => a.animal_id === v);
                    setFormData({
                      ...formData, 
                      sire_id: v === 'none' ? '' : v,
                      sire_name: sire?.name || formData.sire_name
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select sire..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not in system</SelectItem>
                    {potentialParents.males.map((animal) => (
                      <SelectItem key={animal.animal_id} value={animal.animal_id}>
                        {animal.name} ({animal.breed})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(!formData.sire_id || formData.sire_id === 'none') && (
                  <Input
                    className="mt-2"
                    value={formData.sire_name}
                    onChange={(e) => setFormData({...formData, sire_name: e.target.value})}
                    placeholder="Or enter sire name manually"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Litter Size</Label>
              <Input
                type="number"
                value={formData.litter_size}
                onChange={(e) => setFormData({...formData, litter_size: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Breeder Name</Label>
              <Input
                value={formData.breeder_name}
                onChange={(e) => setFormData({...formData, breeder_name: e.target.value})}
                placeholder="Breeder or source"
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes about the litter..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (litter ? 'Update' : 'Create')} Litter
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// View Litter Details Dialog
const ViewLitterDialog = ({ litter, open, onClose, onUpdate }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animals, setAnimals] = useState([]);
  const [availableAnimals, setAvailableAnimals] = useState([]);
  const [showAddAnimal, setShowAddAnimal] = useState(false);
  const [selectedAnimalId, setSelectedAnimalId] = useState('');

  useEffect(() => {
    if (open && litter) {
      fetchLitterDetails();
      fetchAvailableAnimals();
    }
  }, [open, litter]);

  const fetchLitterDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/shelter/litters/${litter.litter_id}`);
      setDetails(response.data);
      setAnimals(response.data.animals || []);
    } catch (error) {
      toast.error('Failed to load litter details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAnimals = async () => {
    try {
      const response = await axios.get('/shelter/animals');
      // Filter out animals already in a litter
      const available = response.data.filter(a => !a.litter_id);
      setAvailableAnimals(available);
    } catch (error) {
      console.error('Failed to fetch animals:', error);
    }
  };

  const handleAddAnimal = async () => {
    if (!selectedAnimalId) return;
    try {
      await axios.post(`/shelter/litters/${litter.litter_id}/add-animal`, { animal_id: selectedAnimalId });
      toast.success('Animal added to litter');
      setSelectedAnimalId('');
      setShowAddAnimal(false);
      fetchLitterDetails();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add animal');
    }
  };

  const handleRemoveAnimal = async (animalId) => {
    try {
      await axios.delete(`/shelter/litters/${litter.litter_id}/remove-animal/${animalId}`);
      toast.success('Animal removed from litter');
      fetchLitterDetails();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to remove animal');
    }
  };

  if (!litter) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {litter.litter_name}
          </DialogTitle>
          {litter.breed && <DialogDescription>{litter.species} - {litter.breed}</DialogDescription>}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Parents */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Parents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-pink-50 rounded-lg">
                    <p className="text-xs text-pink-600 font-medium">Dam (Mother)</p>
                    <p className="font-medium">
                      {details?.dam?.name || litter.dam_name || 'Unknown'}
                    </p>
                    {details?.dam?.breed && (
                      <p className="text-sm text-stone-500">{details.dam.breed}</p>
                    )}
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Sire (Father)</p>
                    <p className="font-medium">
                      {details?.sire?.name || litter.sire_name || 'Unknown'}
                    </p>
                    {details?.sire?.breed && (
                      <p className="text-sm text-stone-500">{details.sire.breed}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Animals in Litter */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">
                    Siblings ({animals.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => setShowAddAnimal(!showAddAnimal)}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showAddAnimal && (
                  <div className="flex gap-2 mb-4 p-3 bg-stone-50 rounded-lg">
                    <Select value={selectedAnimalId} onValueChange={setSelectedAnimalId}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select animal to add..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableAnimals.map((animal) => (
                          <SelectItem key={animal.animal_id} value={animal.animal_id}>
                            {animal.name} ({animal.species} - {animal.gender})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddAnimal} disabled={!selectedAnimalId}>
                      Add
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddAnimal(false)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {animals.length === 0 ? (
                  <p className="text-center text-stone-500 py-4">No animals in this litter yet</p>
                ) : (
                  <div className="space-y-2">
                    {animals.map((animal) => (
                      <div key={animal.animal_id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          {animal.photos?.[0] ? (
                            <img src={animal.photos[0]} alt={animal.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                              <PawPrint className="w-5 h-5 text-stone-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{animal.name}</p>
                            <p className="text-sm text-stone-500">{animal.gender} • {animal.status}</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500"
                          onClick={() => handleRemoveAnimal(animal.animal_id)}
                        >
                          <Unlink className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Litter Info */}
            {(litter.birth_date || litter.breeder_name || litter.notes) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {litter.birth_date && (
                    <p><span className="text-stone-500">Birth Date:</span> {litter.birth_date}</p>
                  )}
                  {litter.breeder_name && (
                    <p><span className="text-stone-500">Breeder:</span> {litter.breeder_name}</p>
                  )}
                  {litter.notes && (
                    <p><span className="text-stone-500">Notes:</span> {litter.notes}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Litter Manager Component
const LitterManager = () => {
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingLitter, setEditingLitter] = useState(null);
  const [viewingLitter, setViewingLitter] = useState(null);
  const [addAnimalLitter, setAddAnimalLitter] = useState(null);

  useEffect(() => {
    fetchLitters();
  }, []);

  const fetchLitters = async () => {
    try {
      const response = await axios.get('/shelter/litters');
      setLitters(response.data);
    } catch (error) {
      toast.error('Failed to load litters');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Litter Management
          </h3>
          <p className="text-sm text-stone-500">Track lineage by linking animals to their litters, dams, and sires</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-1" /> Create Litter
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      ) : litters.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-stone-400 mb-2" />
          <p className="text-stone-600">No litters created yet</p>
          <p className="text-sm text-stone-500 mb-4">Create a litter to start tracking lineage</p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-1" /> Create First Litter
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {litters.map((litter) => (
            <LitterCard 
              key={litter.litter_id} 
              litter={litter}
              onClick={setViewingLitter}
              onAddAnimal={setAddAnimalLitter}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <LitterDialog
        open={showCreateDialog || !!editingLitter}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingLitter(null);
        }}
        onSuccess={fetchLitters}
        litter={editingLitter}
      />

      {/* View Dialog */}
      <ViewLitterDialog
        litter={viewingLitter}
        open={!!viewingLitter}
        onClose={() => setViewingLitter(null)}
        onUpdate={fetchLitters}
      />
    </div>
  );
};

export default LitterManager;
